import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { Plugin } from './PluginContext';
import { useNotificationDispatch } from './NotificationContext';
import apiClient from '../api/client'; // Importa o nosso novo cliente de API

interface AppliedCoupon {
    code: string;
    discount_type: 'percent' | 'fixed';
    discount_value: number;
}

interface CartContextType {
    cartItems: Plugin[];
    addToCart: (plugin: Plugin) => void;
    removeFromCart: (pluginId: string) => void;
    getCartSubtotal: () => number;
    getCartTotal: () => number;
    clearCart: () => void;
    applyCoupon: (couponCode: string) => Promise<{ success: boolean; message: string }>;
    removeCoupon: () => void;
    coupon?: AppliedCoupon;
    discountAmount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
    const [cartItems, setCartItems] = useState<Plugin[]>(() => {
        try {
            const localData = localStorage.getItem('cartItems');
            return localData ? JSON.parse(localData) : [];
        } catch (error) {
            return [];
        }
    });

    const [coupon, setCoupon] = useState<AppliedCoupon | undefined>(undefined);
    const [discountAmount, setDiscountAmount] = useState(0);

    const { showNotification } = useNotificationDispatch();

    const applyCoupon = async (couponCode: string, isRecalculation = false) => {
        if (cartItems.length === 0) {
            return { success: false, message: "Adicione itens ao carrinho para aplicar um cupom." };
        }

        try {
            // Rota refatorada para POST /cart/apply-coupon
            const response = await apiClient.post('/cart/apply-coupon', { couponCode, cartItems });
            const data = response.data;

            setCoupon(data.coupon);
            setDiscountAmount(data.discountAmount);

            if (!isRecalculation) {
                showNotification("Cupom aplicado com sucesso!", 'success');
            }
            return { success: true, message: "Cupom aplicado!" };

        } catch (error: any) {
            removeCoupon(); // Limpa o cupom inválido
            const errorMessage = error.response?.data?.error || "Erro ao aplicar o cupom.";
            if (!isRecalculation) {
                showNotification(errorMessage, 'error');
            }
            return { success: false, message: errorMessage };
        }
    };

    useEffect(() => {
        localStorage.setItem('cartItems', JSON.stringify(cartItems));
        if (coupon) {
            applyCoupon(coupon.code, true); // Recalcula o cupom se o carrinho mudar
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartItems]);

    const addToCart = (plugin: Plugin) => {
        if (!cartItems.find(item => item.id === plugin.id)) {
            setCartItems(prevItems => [...prevItems, plugin]);
            showNotification(`"${plugin.name}" foi adicionado ao carrinho!`);
        }
    };

    const removeFromCart = (pluginId: string) => {
        setCartItems(prevItems => prevItems.filter(item => item.id !== pluginId));
    };

    const getCartSubtotal = () => {
        return cartItems.reduce((total, item) => total + item.price, 0);
    };

    const getCartTotal = () => {
        const subtotal = getCartSubtotal();
        return Math.max(0, subtotal - discountAmount);
    };

    const removeCoupon = () => {
        setCoupon(undefined);
        setDiscountAmount(0);
    };

    const clearCart = () => {
        setCartItems([]);
        removeCoupon();
    };

    const value = { cartItems, addToCart, removeFromCart, getCartSubtotal, getCartTotal, clearCart, applyCoupon, removeCoupon, coupon, discountAmount };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}